import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFileText, FiDownload, FiCopy, FiShare2, FiEye, FiHelpCircle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ExportModal from './ExportModal';
import InterviewQuestionsModal from './InterviewQuestionsModal';
import toast from 'react-hot-toast';

const OutputView = ({ content, isGenerating, formData }) => {
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isInterviewModalOpen, setIsInterviewModalOpen] = useState(false);

  const copyToClipboard = async () => {
    if (!content) return;

    try {
      await navigator.clipboard.writeText(content);
      toast.success('Job posting copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Function to convert markdown-like content to HTML
  const formatContentToHTML = (text) => {
    if (!text) return '';

    let html = text
      // Convert headers
      .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-500">$1</h1>')
      .replace(/^## (.+)$/gm, '<h2 class="text-xl font-semibold text-gray-800 mb-3 mt-6">$1</h2>')
      .replace(/^### (.+)$/gm, '<h3 class="text-lg font-medium text-gray-700 mb-2 mt-4">$1</h3>')
      // Convert bold text
      .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
      // Convert bullet points
      .replace(/^• (.+)$/gm, '<li class="mb-1 text-gray-700">$1</li>')
      // Convert line breaks to paragraphs
      .replace(/\n\n/g, '</p><p class="mb-4 text-gray-700 leading-relaxed">')
      .replace(/\n/g, '<br>');

    // Wrap consecutive list items in ul tags
    html = html.replace(/(<li class="mb-1 text-gray-700">.*?<\/li>)/gs, (match) => {
      const listItems = match.match(/<li class="mb-1 text-gray-700">.*?<\/li>/g);
      if (listItems && listItems.length > 0) {
        return `<ul class="list-disc list-inside mb-4 space-y-1 ml-4">${listItems.join('')}</ul>`;
      }
      return match;
    });

    // Wrap content in paragraphs
    html = `<div class="prose max-w-none"><p class="mb-4 text-gray-700 leading-relaxed">${html}</p></div>`;

    // Clean up empty paragraphs and fix structure
    html = html
      .replace(/<p class="mb-4 text-gray-700 leading-relaxed"><\/p>/g, '')
      .replace(/<p class="mb-4 text-gray-700 leading-relaxed">(<h[1-6])/g, '$1')
      .replace(/(<\/h[1-6]>)<\/p>/g, '$1')
      .replace(/<p class="mb-4 text-gray-700 leading-relaxed">(<ul)/g, '$1')
      .replace(/(<\/ul>)<\/p>/g, '$1')
      .replace(/<br><\/p>/g, '</p>')
      .replace(/<p class="mb-4 text-gray-700 leading-relaxed"><br>/g, '<p class="mb-4 text-gray-700 leading-relaxed">');

    return html;
  };

  const LoadingAnimation = () => (
    <div className="flex items-center justify-center py-16">
      <div className="text-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full mx-auto mb-4"
        />
        <p className="text-gray-600 font-medium">Generating your job posting...</p>
        <p className="text-sm text-gray-500 mt-1">This may take a few moments</p>
      </div>
    </div>
  );

  const EmptyState = () => (
    <div className="flex items-center justify-center py-16">
      <div className="text-center">
        <SafeIcon icon={FiFileText} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 font-medium">No job posting generated yet</p>
        <p className="text-sm text-gray-400 mt-1">Fill in the form and click generate</p>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden h-fit">
      <div className="px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center">
            <SafeIcon icon={FiEye} className="mr-2" />
            Generated Output
          </h2>
          {content && (
            <div className="flex space-x-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={copyToClipboard}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                title="Copy to clipboard"
              >
                <SafeIcon icon={FiCopy} className="h-4 w-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsInterviewModalOpen(true)}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                title="Generate interview questions"
              >
                <SafeIcon icon={FiHelpCircle} className="h-4 w-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsExportModalOpen(true)}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                title="Export options"
              >
                <SafeIcon icon={FiShare2} className="h-4 w-4" />
              </motion.button>
            </div>
          )}
        </div>
      </div>

      <div className="p-6">
        <AnimatePresence mode="wait">
          {isGenerating ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <LoadingAnimation />
            </motion.div>
          ) : content ? (
            <motion.div
              key="content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              {/* Formatted HTML Content Display */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 shadow-sm max-h-96 overflow-y-auto">
                <div 
                  className="formatted-content" 
                  dangerouslySetInnerHTML={{ __html: formatContentToHTML(content) }} 
                />
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={copyToClipboard}
                  className="flex-1 flex items-center justify-center py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                >
                  <SafeIcon icon={FiCopy} className="mr-2 h-4 w-4" />
                  Copy Text
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsInterviewModalOpen(true)}
                  className="flex-1 flex items-center justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                >
                  <SafeIcon icon={FiHelpCircle} className="mr-2 h-4 w-4" />
                  Interview Questions
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsExportModalOpen(true)}
                  className="flex-1 flex items-center justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                >
                  <SafeIcon icon={FiDownload} className="mr-2 h-4 w-4" />
                  Export
                </motion.button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <EmptyState />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        content={content}
        formData={formData}
      />

      <InterviewQuestionsModal
        isOpen={isInterviewModalOpen}
        onClose={() => setIsInterviewModalOpen(false)}
        jobData={content}
        formData={formData}
      />
    </div>
  );
};

export default OutputView;